import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post, patch, del } from '@/lib/apiClient';
import { API_URL } from '@/_config';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SequenceVariant {
  id: string;
  step_id: string;
  variant_label: string;
  subject_line: string | null;
  email_body: string | null;
  created_at: string;
  updated_at: string;
}

export interface SequenceStep {
  id: string;
  sequence_id: string;
  step_number: number;
  step_type: 'follow_up' | 'value_add' | 'break_up' | 'custom';
  step_label: string;
  delay_config: Record<string, unknown> | null;
  variants: SequenceVariant[];
  created_at: string;
  updated_at: string;
}

export interface SequenceOptions {
  reply_handling?: 'stop' | 'continue' | 'move';
  bounce_handling?: 'pause' | 'remove';
  block_on_bounce?: boolean;
  unsubscribe_link?: boolean;
  footer_text?: string;
  sequence_on_reply?: boolean;
  skip_other_sequences?: boolean;
  skip_recently_contacted?: boolean;
}

export interface Sequence {
  id: string;
  organization_id: string;
  created_by: string;
  name: string;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'archived';
  options: SequenceOptions | null;
  activated_at: string | null;
  steps: SequenceStep[];
  created_at: string;
  updated_at: string;
  starred?: boolean;
}

export interface SequenceListResponse {
  sequences: Sequence[];
  counts: { all: number; active: number; draft: number; paused: number; completed: number };
}

export interface SequenceListParams {
  status?: string;
  search?: string;
  sort_by?: string;
  page?: number;
  per_page?: number;
}

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

export function useSequencesQuery(params: SequenceListParams) {
  return useQuery({
    queryKey: ['sequences', params],
    queryFn: () => get<SequenceListResponse>(API_URL.SEQUENCES.LIST, params as Record<string, unknown>),
    placeholderData: (prev) => prev,
  });
}

export function useSequenceQuery(id: string | undefined) {
  return useQuery({
    queryKey: ['sequence', id],
    queryFn: () => get<Sequence>(API_URL.SEQUENCES.GET(id!)),
    enabled: !!id,
  });
}

export interface SequenceAnalytics {
  enrolled: number;
  sent: number;
  opened: number;
  clicked: number;
  seen: number;
  replied: number;
  bounced: number;
  open_rate: number;
  click_rate: number;
  view_rate: number;
  reply_rate: number;
  time_series: { date: string; sent: number; opened: number }[];
  step_analytics: {
    step_id: string;
    step_number: number;
    variant_id: string;
    variant_label: string;
    subject_line: string | null;
    sent: number;
    delivered: number;
    not_replied_yet: number;
    not_replied_pct: string;
    seen: number;
    clicked: number;
    replied: number;
    bounced: number;
  }[];
  activities_total: number;
  activities: {
    type: string;
    contact_name: string | null;
    contact_count: number | null;
    step_label: string;
    occurred_at: string;
  }[];
}

export function useSequenceAnalyticsQuery(
  id: string | undefined,
  params?: { period?: string; activityPage?: number },
) {
  return useQuery({
    queryKey: ['sequence-analytics', id, params?.period, params?.activityPage],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (params?.period) qs.set('period', params.period);
      if (params?.activityPage && params.activityPage > 1) qs.set('activity_page', String(params.activityPage));
      const url = API_URL.SEQUENCES.ANALYTICS(id!) + (qs.toString() ? '?' + qs.toString() : '');
      return get<SequenceAnalytics>(url);
    },
    enabled: !!id,
  });
}

// ---------------------------------------------------------------------------
// Sequence mutation hooks
// ---------------------------------------------------------------------------

export function useCreateSequenceMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      post<Sequence>(API_URL.SEQUENCES.CREATE, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sequences'] });
    },
  });
}

export function useUpdateSequenceMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      patch<Sequence>(API_URL.SEQUENCES.UPDATE(id), data),
    onSuccess: (_res, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sequences'] });
      queryClient.invalidateQueries({ queryKey: ['sequence', variables.id] });
    },
  });
}

export function useDeleteSequenceMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del(API_URL.SEQUENCES.DELETE(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sequences'] });
    },
  });
}

export function useActivateSequenceMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => post<Sequence>(API_URL.SEQUENCES.ACTIVATE(id), {}),
    onSuccess: (_res, id) => {
      queryClient.invalidateQueries({ queryKey: ['sequences'] });
      queryClient.invalidateQueries({ queryKey: ['sequence', id] });
    },
  });
}

export function usePauseSequenceMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => post<Sequence>(API_URL.SEQUENCES.PAUSE(id), {}),
    onSuccess: (_res, id) => {
      queryClient.invalidateQueries({ queryKey: ['sequences'] });
      queryClient.invalidateQueries({ queryKey: ['sequence', id] });
    },
  });
}

export function useDuplicateSequenceMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => post<Sequence>(API_URL.SEQUENCES.DUPLICATE(id), {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sequences'] });
    },
  });
}

export function useArchiveSequenceMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => post<Sequence>(API_URL.SEQUENCES.ARCHIVE(id), {}),
    onSuccess: (_res, id) => {
      queryClient.invalidateQueries({ queryKey: ['sequences'] });
      queryClient.invalidateQueries({ queryKey: ['sequence', id] });
    },
  });
}

export function useStarSequenceMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => post<{ starred: boolean }>(API_URL.SEQUENCES.STAR(id), {}),
    onSuccess: (_res, id) => {
      queryClient.invalidateQueries({ queryKey: ['sequences'] });
      queryClient.invalidateQueries({ queryKey: ['sequence', id] });
    },
  });
}

export function useUnstarSequenceMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => post<{ starred: boolean }>(API_URL.SEQUENCES.UNSTAR(id), {}),
    onSuccess: (_res, id) => {
      queryClient.invalidateQueries({ queryKey: ['sequences'] });
      queryClient.invalidateQueries({ queryKey: ['sequence', id] });
    },
  });
}

// ---------------------------------------------------------------------------
// Step mutation hooks
// ---------------------------------------------------------------------------

export function useAddStepMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sequenceId, step_type, step_label }: { sequenceId: string; step_type: string; step_label?: string }) =>
      post<SequenceStep>(API_URL.SEQUENCES.STEPS(sequenceId), { step_type, step_label }),
    onSuccess: (_res, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sequences'] });
      queryClient.invalidateQueries({ queryKey: ['sequence', variables.sequenceId] });
    },
  });
}

export function useUpdateStepMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sequenceId, stepId, data }: { sequenceId: string; stepId: string; data: Record<string, unknown> }) =>
      patch<SequenceStep>(API_URL.SEQUENCES.STEP(sequenceId, stepId), data),
    onSuccess: (_res, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sequences'] });
      queryClient.invalidateQueries({ queryKey: ['sequence', variables.sequenceId] });
    },
  });
}

export function useDeleteStepMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sequenceId, stepId }: { sequenceId: string; stepId: string }) =>
      del(API_URL.SEQUENCES.STEP(sequenceId, stepId)),
    onSuccess: (_res, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sequences'] });
      queryClient.invalidateQueries({ queryKey: ['sequence', variables.sequenceId] });
    },
  });
}

export function useReorderStepsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sequenceId, step_ids }: { sequenceId: string; step_ids: string[] }) =>
      patch(API_URL.SEQUENCES.REORDER(sequenceId), { step_ids }),
    onSuccess: (_res, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sequences'] });
      queryClient.invalidateQueries({ queryKey: ['sequence', variables.sequenceId] });
    },
  });
}

// ---------------------------------------------------------------------------
// Variant mutation hooks
// ---------------------------------------------------------------------------

export function useAddVariantMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ stepId, sequenceId }: { stepId: string; sequenceId?: string }) =>
      post<SequenceVariant>(API_URL.SEQUENCES.VARIANTS(stepId), {}),
    onSuccess: (_res, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sequences'] });
      if (variables.sequenceId) {
        queryClient.invalidateQueries({ queryKey: ['sequence', variables.sequenceId] });
      }
    },
  });
}

export function useUpdateVariantMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ stepId, variantId, sequenceId, subject_line, email_body }: {
      stepId: string;
      variantId: string;
      sequenceId?: string;
      subject_line?: string;
      email_body?: string;
    }) =>
      patch<SequenceVariant>(API_URL.SEQUENCES.VARIANT(stepId, variantId), { subject_line, email_body }),
    onSuccess: (_res, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sequences'] });
      if (variables.sequenceId) {
        queryClient.invalidateQueries({ queryKey: ['sequence', variables.sequenceId] });
      }
    },
  });
}

export function useDeleteVariantMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ stepId, variantId }: { stepId: string; variantId: string; sequenceId?: string }) =>
      del(API_URL.SEQUENCES.VARIANT(stepId, variantId)),
    onSuccess: (_res, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sequences'] });
      if (variables.sequenceId) {
        queryClient.invalidateQueries({ queryKey: ['sequence', variables.sequenceId] });
      }
    },
  });
}
